/**
 * Split a product name into the card and its art treatment (TRO-327).
 *
 * The client mirror of `backend/src/trovie/domain/tcg/printing_treatment.py`. Both suites execute
 * the cases in `shared/fixtures/printing-treatment.json`, so a change on one side only turns the
 * other suite red (root `AGENTS.md`, "One rule, two languages"). Add a shared case there, not in
 * either test.
 *
 * Some games ship treatments of a card as separate TCGplayer products, each with its own collector
 * number and price, and record the treatment only in the product name as trailing parentheticals.
 * One Magic card in FINAL FANTASY is five products:
 *
 *   #10   Cloud, Midgar Mercenary
 *   #375  Cloud, Midgar Mercenary (Borderless)
 *   #427  Cloud, Midgar Mercenary (Extended Art)
 *   #520  Cloud, Midgar Mercenary (Borderless) (Surge Foil)
 *   #564  Cloud, Midgar Mercenary (Borderless) (Chocobo Track Foil)
 *
 * Those are one card to an operator holding a stack, but five unrelated candidates to a filter that
 * compares names literally. `catalogConvention` decides which games follow the convention.
 *
 * Pokémon ships them too, in a shape a scan cannot tell apart (TRO-296):
 *
 *   031/182  Team Rocket's Moltres ex                                    SV10: Destined Rivals
 *   031/182  Team Rocket's Moltres ex - 31/182 (Destined Rivals Stamp)   Miscellaneous Cards
 *
 * A stamp is a few millimetres of foil, so recognition answers a photo of the second with the
 * first. The stamped product must stay among the alternatives so the operator can correct the
 * price.
 */

import { catalogConvention } from "@/lib/catalog/catalogConvention";
import { type CardText, parsePrintedNumber } from "@/lib/catalog/collectorNumber";

/**
 * One trailing "(...)" group, anchored to the end. Applied repeatedly so stacked treatments peel
 * off one at a time. The inner class excludes brackets so a nested parenthesis can never be
 * consumed, and requires a character so an empty "()" stays part of the name.
 */
const TRAILING_PAREN = /\s*\(([^()]+)\)\s*$/;

/**
 * The provider's number disambiguator: a spaced dash and one trailing token that might be a
 * collector number. The dash must be spaced on both sides so a hyphenated card name
 * (`Poncho-wearing Pikachu`) is untouched, while the token may carry a dash because many Pokémon
 * promos are numbered over a hyphenated denominator (`- 37/SM-P`, `- 143/S-P`). The pattern is
 * loose on purpose: parsing the token decides whether it is a number. The server mirror spells the
 * end anchor `\Z`, which is what JavaScript's `$` means without the `m` flag.
 */
const TRAILING_DASHED_TOKEN = /[ \t]+-[ \t]+([A-Za-z0-9][A-Za-z0-9/-]*)[ \t]*$/;

export type PrintingName = {
  /** The card, with every trailing treatment removed. */
  base: string;
  /** Treatments in printed order (`Borderless` before `Surge Foil`), which is also the order a
   *  picker should show. */
  treatments: string[];
  /** The treatments as one string for a chip (`"Borderless · Surge Foil"`), `""` when plain. */
  label: string;
};

/**
 * Splits `name` into its base card name and trailing treatments.
 *
 * This does not judge whether a suffix is really a treatment: it peels
 * `Ditto - 038/113 (Mr. Mime)` exactly as it peels `(Borderless)`. `sameCard` answers whether that
 * means the same card, using the game, because no string rule can tell a treatment from part of a
 * name without the convention.
 */
export function splitPrintingName(name: string | null | undefined): PrintingName {
  let remaining = (name ?? "").trim();
  const treatments: string[] = [];
  for (;;) {
    const match = TRAILING_PAREN.exec(remaining);
    if (!match) break;
    treatments.push(match[1].trim());
    remaining = remaining.slice(0, match.index).trimEnd();
  }
  treatments.reverse(); // Peeled right to left; restore printed order.
  return { base: remaining, treatments, label: treatments.join(" · ") };
}

/**
 * Splits `name` for display under `game`'s own convention (TRO-495).
 *
 * The rendering counterpart to `sameCard`, with the same game gate. Stripping a treatment out of a
 * name is only right where the game puts it there: rendering `Mr. Mime (Team Rocket's)` as
 * `Mr. Mime` plus a chip claims two different cards are one. An unknown or missing game keeps its
 * whole name.
 */
export function displayPrintingName(
  name: string | null | undefined,
  game: string | null | undefined,
): PrintingName {
  const whole = { base: (name ?? "").trim(), treatments: [], label: "" };
  if (!catalogConvention(game).treatmentInName) return whole;
  const split = splitPrintingName(name);
  // A name that is only a parenthetical is not a card name plus a treatment, so it is shown whole.
  // This also keeps `base` non-empty wherever the name is, so no caller needs an empty-name rule.
  return split.base ? split : whole;
}

/**
 * Returns `name` without the provider's number disambiguator, when it repeats `number`.
 *
 * TCGTracking appends a card's own collector number to its product name where a set holds two
 * products of one name (`Team Rocket's Moltres ex - 31/182` beside a `number` of `031/182`). Left
 * in, it hides a card's other printings from every surface that groups by name.
 *
 * Both sides go through `parsePrintedNumber`, so leading zeros and the `/<set size>` tail are
 * ignored and `70` never reads as `70a`. Nothing is stripped without a `number` proving the token
 * is a repeat, which keeps `Mr. Mime - 2013` (collector number 47/116) intact.
 */
export function withoutRepeatedCollectorNumber(
  name: string | null | undefined,
  number?: string | null,
): string {
  const text = (name ?? "").trim();
  const printed = parsePrintedNumber(number);
  if (!printed) return text;
  const match = TRAILING_DASHED_TOKEN.exec(text);
  if (!match || parsePrintedNumber(match[1])?.canonical !== printed.canonical) return text;
  return text.slice(0, match.index).trimEnd();
}

/**
 * Whether two product names are the same card in different treatments.
 *
 * Falls back to exact case-insensitive name comparison for any game that `catalogConvention` does
 * not mark as carrying its treatment in the name.
 *
 * Each side is a `CardText`, the name with its own collector number, because the number identifies
 * the provider's disambiguator inside the name. The two numbers are never compared to each other:
 * sharing a number does not make two cards one, and a treatment with its own number is still the
 * same card. Without a `number`, only the name is compared. Each pair is one object so transposed
 * arguments fail to compile.
 */
export function sameCard(a: CardText, b: CardText, game?: string | null): boolean {
  const namedTreatments = !!game && catalogConvention(game).treatmentInName;
  return comparisonKey(a, namedTreatments) === comparisonKey(b, namedTreatments);
}

/**
 * Reduces one product name to what identifies its card. Treatments peel before the number
 * disambiguator because the provider writes them in that order:
 * `Team Rocket's Moltres ex - 31/182 (Destined Rivals Stamp)` only reveals its `- 31/182` once the
 * stamp is off.
 */
function comparisonKey(card: CardText, namedTreatments: boolean): string {
  const base = namedTreatments ? splitPrintingName(card.name).base : (card.name ?? "").trim();
  // `toLowerCase`, never `toLocaleLowerCase`: the latter reads the device locale, and a Turkish
  // locale maps `I` to the dotless `ı`, so "Iron Valiant" and "IRON VALIANT" would stop matching
  // there while the server still groups them (TRO-296).

  return withoutRepeatedCollectorNumber(base, card.number).toLowerCase();
}
